import type { FC, ReactNode } from "react";
import { createContext, useContext } from "react";

import { CAGlobals, USGlobals } from "@shared/data/globals";

enum GlobalContextUserRole {
  CLERK = "clerk",
  PATIENT = "patient",
  PRACTITIONER = "practitioner",
  ROLELESS = "roleless",
  UNAUTHENTICATED = "unauthenticated",
}
interface GlobalContextType {
  adminImpersonating?: boolean;
  country: string;
  role: GlobalContextUserRole;
  isFromWellevate?: boolean;
  isFromEmersonEcologics?: boolean;
  e2e: boolean;
}

const GlobalContextDefault: GlobalContextType = {
  adminImpersonating: false,
  country: "United States",
  role: GlobalContextUserRole.UNAUTHENTICATED,
  isFromWellevate: false,
  isFromEmersonEcologics: false,
  e2e: false,
};

const GlobalContext = createContext<GlobalContextType>(GlobalContextDefault);

/**
 * @deprecated Please use {@link https://git.fullscript.io/developers/hw-admin/-/blob/staging/app/javascript/shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig.ts useSharedGlobalConfig}
 */
const useGlobalConfig = () => {
  const { country, adminImpersonating, role, isFromWellevate, isFromEmersonEcologics, e2e } =
    useContext(GlobalContext);

  const isPractitionerOnly = role?.toLowerCase() === GlobalContextUserRole.PRACTITIONER;
  const isClerkOnly = role?.toLowerCase() === GlobalContextUserRole.CLERK;
  const isClinic = isPractitionerOnly || isClerkOnly;

  const isPatient = role?.toLowerCase() === GlobalContextUserRole.PATIENT;
  const isUnauthenticated = role?.toLowerCase() === GlobalContextUserRole.UNAUTHENTICATED;

  const isCanada = country === CAGlobals.country;
  const isUS = country === USGlobals.country;

  return {
    country,
    isAdminImpersonating: adminImpersonating,
    isCanada,
    isUS,
    isFromWellevate,
    isFromEmersonEcologics,
    isPractitionerOnly,
    isClerkOnly,
    isClinic,
    isPatient,
    isUnauthenticated,
    e2e,
  };
};

interface Props {
  adminImpersonating?: boolean;
  country: string;
  role: GlobalContextUserRole;
  isFromWellevate?: boolean;
  isFromEmersonEcologics?: boolean;
  e2e: boolean;
  children: ReactNode;
}

const GlobalContextProvider: FC<Props> = ({
  children,
  adminImpersonating,
  role,
  country,
  isFromWellevate,
  isFromEmersonEcologics,
  e2e,
}) => {
  return (
    <GlobalContext.Provider
      value={{ country, adminImpersonating, role, isFromWellevate, isFromEmersonEcologics, e2e }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export {
  GlobalContext,
  GlobalContextUserRole,
  GlobalContextProvider,
  useGlobalConfig,
  GlobalContextDefault,
};
export type { GlobalContextType };
